/**
 * SCD2ConfigPanel — SCD2-specific configuration fields for a Job.
 *
 * Rendered inside StrategyPanel ONLY when job.loadStrategy === "scd2".
 *
 * Fields:
 *   - scdTrackedColumns: comma-separated column names that trigger new version rows
 *   - scdEffectiveDateCol: metadata column name (default: effective_date)
 *   - scdExpiryDateCol: metadata column name (default: expiry_date)
 *   - scdCurrentFlagCol: metadata column name (default: is_current)
 *   - scdHashCol: metadata column name (default: _row_hash)
 *
 * Save calls PATCH /jobs/{jid} with all 5 SCD fields in snake_case.
 * Follows the same onSaved / onDirtyChange pattern as all other job-editor panels.
 */

import { useCallback, useEffect, useId, useState } from "react";
import { Loader2, Save } from "lucide-react";
import type { PEJob } from "../../../features/pipeline-engineer/types";
import { updateJob } from "../../../features/pipeline-engineer/api/pe-client";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Parse a comma-separated string into a trimmed, non-empty string array. */
function parseCSV(raw: string): string[] {
  return raw
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

/** Serialize a string array to comma-separated display string. */
function toCSV(values: string[] | null): string {
  return values?.join(", ") ?? "";
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SCD2ConfigPanelProps {
  job: PEJob;
  pipelineId: string;
  jobId: string;
  onSaved: (updated: PEJob) => void;
  onDirtyChange: (isDirty: boolean) => void;
  onConflict: () => void;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SCD2ConfigPanel({
  job,
  pipelineId,
  jobId,
  onSaved,
  onDirtyChange,
  onConflict,
}: SCD2ConfigPanelProps) {
  const uid = useId();

  const [trackedColumnsRaw, setTrackedColumnsRaw] = useState(
    toCSV(job.scdTrackedColumns),
  );
  const [effectiveDateCol, setEffectiveDateCol] = useState(
    job.scdEffectiveDateCol ?? "",
  );
  const [expiryDateCol, setExpiryDateCol] = useState(
    job.scdExpiryDateCol ?? "",
  );
  const [currentFlagCol, setCurrentFlagCol] = useState(
    job.scdCurrentFlagCol ?? "",
  );
  const [hashCol, setHashCol] = useState(job.scdHashCol ?? "");

  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  // ── Sync local state when job is saved (updatedAt changes) ──────────────
  // Use job.id + job.updatedAt (not the whole `job` object) so we don't reset
  // on every parent re-render — only when the server round-trip actually updates.
  useEffect(() => {
    setTrackedColumnsRaw(toCSV(job.scdTrackedColumns));
    setEffectiveDateCol(job.scdEffectiveDateCol ?? "");
    setExpiryDateCol(job.scdExpiryDateCol ?? "");
    setCurrentFlagCol(job.scdCurrentFlagCol ?? "");
    setHashCol(job.scdHashCol ?? "");
  }, [job.id, job.updatedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Dirty detection ────────────────────────────────────────────────────────
  const isDirty =
    trackedColumnsRaw !== toCSV(job.scdTrackedColumns) ||
    effectiveDateCol !== (job.scdEffectiveDateCol ?? "") ||
    expiryDateCol !== (job.scdExpiryDateCol ?? "") ||
    currentFlagCol !== (job.scdCurrentFlagCol ?? "") ||
    hashCol !== (job.scdHashCol ?? "");

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  // ── Column mapping suggestions for datalist ────────────────────────────────
  const suggestedColumns = job.columnMappings.map((m) => m.tgtCol);

  // ── Save ───────────────────────────────────────────────────────────────────
  const handleSave = useCallback(async () => {
    setValidationError(null);
    setSaveError(null);

    const parsed = parseCSV(trackedColumnsRaw);
    if (parsed.length === 0) {
      setValidationError("Tracked columns are required for SCD2.");
      return;
    }

    setSaving(true);
    try {
      const updated = await updateJob(pipelineId, jobId, {
        scd_tracked_columns: parsed,
        scd_effective_date_col: effectiveDateCol.trim() || undefined,
        scd_expiry_date_col: expiryDateCol.trim() || undefined,
        scd_current_flag_col: currentFlagCol.trim() || undefined,
        scd_hash_col: hashCol.trim() || undefined,
      });
      onSaved(updated);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Save failed.";
      if (msg.includes("409")) {
        onConflict();
      } else {
        setSaveError(msg);
      }
    } finally {
      setSaving(false);
    }
  }, [
    pipelineId,
    jobId,
    trackedColumnsRaw,
    effectiveDateCol,
    expiryDateCol,
    currentFlagCol,
    hashCol,
    onSaved,
    onConflict,
  ]);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="border-t border-white/10 pt-4 mt-4 space-y-4">
      <h3 className="text-xs font-semibold text-white/70 uppercase tracking-wider">
        SCD2 Configuration
      </h3>

      {/* Tracked Columns */}
      <div className="space-y-1">
        <label
          htmlFor={`${uid}-tracked`}
          className="block text-xs font-medium text-white/50"
        >
          Tracked Columns
        </label>
        <input
          id={`${uid}-tracked`}
          type="text"
          list={`${uid}-tracked-list`}
          value={trackedColumnsRaw}
          onChange={(e) => setTrackedColumnsRaw(e.target.value)}
          placeholder="e.g. first_name, last_name, email"
          className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-brand-500/50 font-mono"
          aria-describedby={`${uid}-tracked-help`}
        />
        <datalist id={`${uid}-tracked-list`}>
          {suggestedColumns.map((col) => (
            <option key={col} value={col} />
          ))}
        </datalist>
        {validationError !== null ? (
          <p
            id={`${uid}-tracked-help`}
            className="text-red-400 text-xs"
            role="alert"
          >
            {validationError}
          </p>
        ) : (
          <p id={`${uid}-tracked-help`} className="text-xs text-white/30">
            Tracked columns trigger a new history row when their value changes.
            Non-tracked columns are updated in-place. Separate with commas.
          </p>
        )}
      </div>

      {/* Metadata column names */}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-1">
          <label
            htmlFor={`${uid}-effective`}
            className="block text-xs font-medium text-white/50"
          >
            Effective Date Column
          </label>
          <input
            id={`${uid}-effective`}
            type="text"
            value={effectiveDateCol}
            onChange={(e) => setEffectiveDateCol(e.target.value)}
            placeholder="effective_date"
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-brand-500/50 font-mono"
          />
        </div>

        <div className="space-y-1">
          <label
            htmlFor={`${uid}-expiry`}
            className="block text-xs font-medium text-white/50"
          >
            Expiry Date Column
          </label>
          <input
            id={`${uid}-expiry`}
            type="text"
            value={expiryDateCol}
            onChange={(e) => setExpiryDateCol(e.target.value)}
            placeholder="expiry_date"
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-brand-500/50 font-mono"
          />
        </div>

        <div className="space-y-1">
          <label
            htmlFor={`${uid}-flag`}
            className="block text-xs font-medium text-white/50"
          >
            Current Flag Column
          </label>
          <input
            id={`${uid}-flag`}
            type="text"
            value={currentFlagCol}
            onChange={(e) => setCurrentFlagCol(e.target.value)}
            placeholder="is_current"
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-brand-500/50 font-mono"
          />
        </div>

        <div className="space-y-1">
          <label
            htmlFor={`${uid}-hash`}
            className="block text-xs font-medium text-white/50"
          >
            Hash Column
          </label>
          <input
            id={`${uid}-hash`}
            type="text"
            value={hashCol}
            onChange={(e) => setHashCol(e.target.value)}
            placeholder="_row_hash"
            className="w-full px-3 py-2 bg-white/5 border border-white/10 rounded-lg text-xs text-white placeholder-white/20 focus:outline-none focus:ring-1 focus:ring-brand-500/50 font-mono"
          />
        </div>
      </div>

      {/* Save error */}
      {saveError !== null && (
        <p className="text-red-400 text-xs" role="alert">
          {saveError}
        </p>
      )}

      {/* Save button */}
      <div className="flex justify-end">
        <button
          type="button"
          onClick={() => void handleSave()}
          disabled={!isDirty || saving}
          className="flex items-center gap-1.5 px-4 py-2 bg-brand-500 hover:bg-brand-600 text-white rounded-lg text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {saving ? (
            <Loader2 size={13} className="animate-spin" aria-hidden="true" />
          ) : (
            <Save size={13} aria-hidden="true" />
          )}
          Save SCD2 Config
        </button>
      </div>
    </div>
  );
}
